import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';

type View = 'my' | 'agency';

interface UseListingManagementViewProps {
  userDetails: any;
  onLogout: () => void;
}

export const useListingManagementView = ({ userDetails, onLogout }: UseListingManagementViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeItem, setItems, setFooterItems, setActiveItem } = useSidebar();
  const initialView = (location.state as any)?.view ?? 'my';
  const [currentView, setCurrentView] = useState<View>(initialView);

  const sidebarItems = useMemo(() => {
    const canSeeAgency = userDetails?.role === 'Manager' || userDetails?.role === 'Admin' || userDetails?.role === 'Super Admin';
    const items: { id: string; label: string; icon: string; onClick: () => void }[] = [{ 
      id: 'my',     
      label: 'My Listings',    
      icon: '🏠', 
      onClick: () => navigate('/ListingManagement', { state: { view: 'my' } }) 
    }];
    if (canSeeAgency) {
      items.push({ 
        id: 'agency', 
        label: 'Agency Listings', 
        icon: '🏢', 
        onClick: () => navigate('/ListingManagement', { state: { view: 'agency' } }) 
      });
    }
    return items;
  }, [userDetails?.role, navigate]);

  const sidebarFooterItems = useMemo(() => [
    { id: 'help', label: 'Help', to: '/ContactUs', icon: '⁉️' },
    { id: 'privacy', label: 'Privacy Policy', to: '/PrivacyPolicy', icon: '⛓️‍💥' },
    { id: 'logout', label: 'Logout', onClick: () => { onLogout(); }, icon: '💤' },
  ], [onLogout]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!userDetails) {
      navigate('/LoginRegister', { replace: true });
    }
  }, [userDetails, navigate]);

  useLayoutEffect(() => {
    setItems(sidebarItems);
    setFooterItems(sidebarFooterItems);
    setActiveItem(initialView);
  }, [setItems, setFooterItems, setActiveItem, sidebarItems, sidebarFooterItems]);

  useEffect(() => {
    if (activeItem === 'my' || activeItem === 'agency') {
      setCurrentView(activeItem);
    }
  }, [activeItem]);

  return { currentView };
};
